import { Fragment, useCallback, useEffect, useState } from 'react';
import { Ban, CheckCircle2, Loader2, Search, ShieldCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { API, EmptyBox, ErrorBox, LoadingBox, TabHeader, asList, fmtDate, inputCls } from './AdminShared';

export const AdminUsers = ({ authHeaders, tx }) => {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openFor, setOpenFor] = useState(null);
  const [reason, setReason] = useState('');
  const [role, setRole] = useState('regular_user');
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (query) => {
      setLoading(true);
      setError(false);
      try {
        const qs = query ? `&q=${encodeURIComponent(query)}` : '';
        const res = await fetch(`${API}/api/admin/app-users?limit=50${qs}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('users');
        setUsers(asList(await res.json(), ['users', 'items']));
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [authHeaders],
  );

  useEffect(() => {
    load('');
  }, [load]);

  const doPost = async (path, body) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error('action');
      toast.success(tx.users.actionDone);
      setOpenFor(null);
      setReason('');
      await load(q);
    } catch {
      toast.error(tx.users.actionFail);
    } finally {
      setBusy(false);
    }
  };

  const banUser = (u) => {
    if (reason.trim().length < 3 || busy) return;
    if (!window.confirm(`${tx.users.banConfirm} ${u.display_name || u.email}?`)) return;
    doPost(`/api/admin/app-users/${u.user_id}/ban`, { reason: reason.trim() });
  };

  const unbanUser = (u) => {
    if (busy) return;
    if (!window.confirm(`${tx.users.unbanConfirm} ${u.display_name || u.email}?`)) return;
    doPost(`/api/admin/app-users/${u.user_id}/unban`);
  };

  const setUserRole = (u) => {
    if (busy) return;
    if (!window.confirm(`${tx.users.roleConfirm} ${u.display_name || u.email}?`)) return;
    doPost(`/api/admin/app-users/${u.user_id}/role`, { role });
  };

  const roleLabel = (r) =>
    r === 'super_admin' ? tx.users.roleSuper : r === 'moderator' ? tx.users.roleModerator : tx.users.roleRegular;

  return (
    <section data-testid="app-users-section">
      <TabHeader icon={Users} title={tx.users.title} count={users.length} onRefresh={() => load(q)} refreshLabel={tx.common.refresh} testid="app-users" />

      <form
        data-testid="users-search-form"
        onSubmit={(e) => {
          e.preventDefault();
          load(q.trim());
        }}
        className="mt-6 flex gap-3 max-w-xl"
      >
        <input
          type="text"
          data-testid="users-search-input"
          placeholder={tx.users.searchPlaceholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className={inputCls}
        />
        <button
          type="submit"
          data-testid="users-search-btn"
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-krug-gold px-5 py-2.5 text-sm font-bold text-krug-navy hover:bg-krug-goldbright transition-colors duration-300"
        >
          <Search size={15} />
          {tx.users.search}
        </button>
      </form>

      <div className="mt-6">
        {loading && <LoadingBox text={tx.common.loading} testid="app-users-loading" />}
        {!loading && error && (
          <ErrorBox text={tx.common.unavailable} retryText={tx.common.retry} onRetry={() => load(q)} testid="app-users-error" />
        )}
        {!loading && !error && users.length === 0 && <EmptyBox text={tx.users.empty} testid="app-users-empty" />}
        {!loading && !error && users.length > 0 && (
          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table data-testid="app-users-table" className="w-full text-left text-sm">
              <thead>
                <tr className="bg-white/[0.04] text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5 font-bold">{tx.users.colUser}</th>
                  <th className="px-5 py-3.5 font-bold">{tx.users.colRole}</th>
                  <th className="px-5 py-3.5 font-bold">{tx.users.colStatus}</th>
                  <th className="px-5 py-3.5 font-bold">{tx.users.colVerified}</th>
                  <th className="px-5 py-3.5 font-bold">{tx.users.colDate}</th>
                  <th className="px-5 py-3.5 font-bold">{tx.users.actions}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <Fragment key={u.user_id}>
                    <tr className="border-t border-white/5 hover:bg-white/[0.03] transition-colors duration-200">
                      <td className="px-5 py-3.5">
                        <p className="text-white font-medium">{u.display_name || '—'}</p>
                        <p className="text-xs text-slate-500">{u.email || ''}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <span
                          className={`rounded-full px-3 py-1 text-[11px] font-bold ${
                            u.role === 'super_admin'
                              ? 'bg-krug-gold/15 text-krug-goldbright'
                              : u.role === 'moderator'
                                ? 'bg-krug-teal/15 text-krug-tealbright'
                                : 'bg-white/10 text-slate-400'
                          }`}
                        >
                          {roleLabel(u.role)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        {u.banned ? (
                          <span data-testid={`user-banned-${u.user_id}`} className="rounded-full bg-red-500/15 px-3 py-1 text-[11px] font-bold text-red-400">
                            {tx.users.banned}
                          </span>
                        ) : (
                          <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-[11px] font-bold text-emerald-400">
                            {tx.users.active}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {u.verified ? (
                          <CheckCircle2 size={16} className="text-krug-tealbright" />
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-slate-500 text-xs">{fmtDate(u.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <button
                          type="button"
                          data-testid={`user-actions-${u.user_id}`}
                          onClick={() => {
                            setOpenFor(openFor === u.user_id ? null : u.user_id);
                            setReason('');
                            setRole(u.role || 'regular_user');
                          }}
                          className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-bold text-slate-300 hover:text-white hover:border-krug-gold/50 transition-colors duration-300"
                        >
                          {tx.users.actions}
                        </button>
                      </td>
                    </tr>
                    {openFor === u.user_id && (
                      <tr data-testid={`user-panel-${u.user_id}`} className="border-t border-white/5 bg-white/[0.02]">
                        <td colSpan={6} className="px-5 py-4">
                          <div className="flex flex-wrap items-end gap-4">
                            {u.banned ? (
                              <button
                                type="button"
                                data-testid={`user-unban-${u.user_id}`}
                                disabled={busy}
                                onClick={() => unbanUser(u)}
                                className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-4 py-2.5 text-xs font-bold text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-50 transition-colors duration-300"
                              >
                                <CheckCircle2 size={14} />
                                {tx.users.unban}
                              </button>
                            ) : (
                              <div className="flex items-end gap-3">
                                <input
                                  type="text"
                                  data-testid={`user-ban-reason-${u.user_id}`}
                                  placeholder={tx.users.banReason}
                                  value={reason}
                                  onChange={(e) => setReason(e.target.value)}
                                  className={`${inputCls} max-w-xs !py-2.5`}
                                />
                                <button
                                  type="button"
                                  data-testid={`user-ban-${u.user_id}`}
                                  disabled={busy || reason.trim().length < 3}
                                  onClick={() => banUser(u)}
                                  className="inline-flex items-center gap-2 rounded-full bg-red-500/15 border border-red-400/40 px-4 py-2.5 text-xs font-bold text-red-400 hover:bg-red-500/25 disabled:opacity-50 transition-colors duration-300"
                                >
                                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                                  {tx.users.ban}
                                </button>
                              </div>
                            )}
                            <div className="flex items-end gap-3">
                              <select
                                data-testid={`user-role-select-${u.user_id}`}
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                className="rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-xs font-bold text-slate-300 outline-none cursor-pointer"
                              >
                                <option value="regular_user" className="bg-krug-navy">{tx.users.roleRegular}</option>
                                <option value="moderator" className="bg-krug-navy">{tx.users.roleModerator}</option>
                                <option value="super_admin" className="bg-krug-navy">{tx.users.roleSuper}</option>
                              </select>
                              <button
                                type="button"
                                data-testid={`user-role-set-${u.user_id}`}
                                disabled={busy}
                                onClick={() => setUserRole(u)}
                                className="inline-flex items-center gap-2 rounded-full bg-krug-teal px-4 py-2.5 text-xs font-bold text-white hover:bg-krug-tealbright hover:text-krug-navy disabled:opacity-50 transition-colors duration-300"
                              >
                                <ShieldCheck size={14} />
                                {tx.users.roleSet}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminUsers;
